import Cookies from 'js-cookie';

const COOKIE_NAME = 'cocopool_utm';
const COOKIE_EXPIRES_DAYS = 30;
const MAX_UTM_LENGTH = 200; // Prevent malicious long strings

const normalizeValue = value => {
  if (typeof value !== 'string') {
    return null;
  }
  const trimmed = value.trim();

  // Validate length to prevent malicious inputs
  if (trimmed.length === 0 || trimmed.length > MAX_UTM_LENGTH) {
    return null;
  }

  return trimmed;
};

const safeJsonParse = value => {
  if (!value) return null;
  try {
    const parsed = JSON.parse(value);
    return parsed && typeof parsed === 'object' ? parsed : null;
  } catch (e) {
    return null;
  }
};

const buildPayload = (utmSource, utmMedium, utmCampaign) => {
  const source = normalizeValue(utmSource);
  const medium = normalizeValue(utmMedium);
  const campaign = normalizeValue(utmCampaign);

  if (!source && !medium && !campaign) {
    return null;
  }

  const landingUrl = typeof window !== 'undefined' ? window.location.href : null;

  return {
    utm_source: source,
    utm_medium: medium,
    utm_campaign: campaign,
    timestamp: new Date().toISOString(),
    landing_url: landingUrl,
  };
};

const getUtmFromUrl = () => {
  if (typeof window === 'undefined') return null;
  const search = window.location.search || '';
  const params = new URLSearchParams(search);

  const utmSource = params.get('utm_source');
  const utmMedium = params.get('utm_medium');
  const utmCampaign = params.get('utm_campaign');

  return buildPayload(utmSource, utmMedium, utmCampaign);
};

const parseUtmzCookie = value => {
  if (!value) return null;
  const parts = value.split('.');
  if (parts.length < 5) return null;

  const campaignData = parts.slice(4).join('.');
  if (!campaignData) return null;

  const pairs = campaignData.split('|');
  const map = pairs.reduce((acc, pair) => {
    const [key, ...rest] = pair.split('=');
    if (!key || rest.length === 0) return acc;
    acc[key] = rest.join('=');
    return acc;
  }, {});

  return buildPayload(map.utmcsr, map.utmcmd, map.utmccn);
};

const getUtmFromGaCookie = () => {
  if (typeof window === 'undefined') return null;
  const utmz = Cookies.get('__utmz');
  return parseUtmzCookie(utmz);
};

const getUtmPayload = () => {
  const fromUrl = getUtmFromUrl();
  if (fromUrl) return fromUrl;
  return getUtmFromGaCookie();
};

const readUtmCookie = () => {
  if (typeof window === 'undefined') return null;
  const raw = Cookies.get(COOKIE_NAME);
  return safeJsonParse(raw);
};

const shouldLog = () => typeof window !== 'undefined' && window.__UTM_DEBUG__ === true;

const writeUtmCookie = value => {
  if (typeof window === 'undefined') return;

  try {
    // Scope the cookie to the current host only. We intentionally do NOT set
    // a `domain` attribute: setting it via "last two parts of the hostname"
    // breaks on hosts that are themselves a public suffix (e.g. *.onrender.com,
    // *.vercel.app, *.github.io), where browsers silently reject the cookie.
    // The app runs on a single host per environment, so host-only is sufficient.
    const cookieOptions = {
      expires: COOKIE_EXPIRES_DAYS,
      path: '/',
      sameSite: 'Lax',
    };

    Cookies.set(COOKIE_NAME, JSON.stringify(value), cookieOptions);
  } catch (e) {
    if (shouldLog()) {
      console.warn('Failed to write UTM cookie:', e);
    }
  }
};

const isFirstUtmExpired = firstutm => {
  if (!firstutm || !firstutm.timestamp) return true;
  const parsed = Date.parse(firstutm.timestamp);
  if (Number.isNaN(parsed)) return true;
  const ageMs = Date.now() - parsed;
  return ageMs > COOKIE_EXPIRES_DAYS * 24 * 60 * 60 * 1000;
};

const areUtmSame = (a, b) => {
  if (!a || !b) return false;
  return (
    (a.utm_source || null) === (b.utm_source || null) &&
    (a.utm_medium || null) === (b.utm_medium || null) &&
    (a.utm_campaign || null) === (b.utm_campaign || null)
  );
};

export const updateUtmCookieFromLanding = () => {
  const payload = getUtmPayload();
  if (shouldLog()) {
    console.log('UTM capture payload:', payload);
  }
  if (!payload) return null;

  const existing = readUtmCookie();
  if (shouldLog()) {
    console.log('UTM cookie before update:', existing);
  }
  const shouldReset = !existing || !existing.firstutm || isFirstUtmExpired(existing.firstutm);
  if (shouldReset) {
    const nextValue = { firstutm: payload };
    writeUtmCookie(nextValue);
    if (shouldLog()) {
      console.log('UTM cookie set firstutm:', nextValue);
    }
    return nextValue;
  }

  const latest = existing.lastutm || existing.firstutm;
  if (!areUtmSame(latest, payload)) {
    const nextValue = { ...existing, lastutm: payload };
    writeUtmCookie(nextValue);
    if (shouldLog()) {
      console.log('UTM cookie set lastutm:', nextValue);
    }
    return nextValue;
  }

  if (shouldLog()) {
    console.log('UTM cookie unchanged');
  }
  return existing;
};

export const getUtmMetadataFromCookie = () => {
  const existing = readUtmCookie();
  if (!existing || !existing.firstutm || isFirstUtmExpired(existing.firstutm)) {
    return {};
  }

  const metadata = { firstutm: existing.firstutm };
  if (existing.lastutm) {
    metadata.lastutm = existing.lastutm;
  }
  return metadata;
};
